import { groupBy, map, min, sumBy, uniq } from "lodash";
import moment from "moment";

import { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import { LocationType } from "@busbud/int-schemas/udm";

import { isNotNil } from "@app/helpers/is-not-nil";
import { IncludedFees } from "@app/helpers/price";
import type { Price } from "@app/helpers/price";
import type { NextDepartureStats, SeoVeRoute } from "@app/services/key-stats";
import type {
  DeparturesPerDate,
  PriceCalendarDeparture,
  TopDeparturesPerDate
} from "@app/services/price-calendar-service-api";
import { DepartureTypes } from "@app/services/price-calendar-service-api";
import type { SeoVeRouteType } from "@app/services/seo-visibility-api";
import type { TranslatorService } from "@app/services/translator";
import type { CurrencyCode } from "@app/types/currency";
import type {
  NapiDeparture,
  NapiDepartureBase,
  NapiDepartureResults,
  NapiPrices
} from "@app/types/napi-departures-types";
import type { NapiLocation } from "@app/types/napi-geo-entity-types";
import type { NapiV2CartCharges } from "@app/types/napi-v2-charges-types";

import type { SortComparatorCriteria } from "../../modules/results/utils/sort";

import {
  baseSortComparator,
  CHEAPEST_BASE
} from "../../modules/results/utils/sort";
import { adaptDepartureToSortItem } from "../../modules/results/utils/sort-adapters";
import { computeQueryString } from "../../utils";
import { getMomentDurationInMinutes } from "../time";

export type NextDeparture = SeoVeRoute & {
  route_type: SeoVeRouteType;
  departures: PriceCalendarDeparture[];
  key_stats: NextDepartureStats;
};

type Dollars = number;
type Cents = number;

export type NextDepartures = {
  buses_per_day: number;
  min_price: Dollars;
  currency: CurrencyCode;
  operator_names: string[];
  routes: NextDeparture[];
};

export function findByIdOrThrow<K, T extends { id: K }>(
  id: K,
  collection: readonly T[]
): T {
  const result = findById(id, collection);

  if (!result) {
    throw new Error("Can't find ID in collection");
  }

  return result;
}

export function findById<K, T extends { id: K }>(
  id: K,
  collection: readonly T[]
): T | undefined {
  return collection.find(element => element.id === id);
}

export function minBy<T>(
  collection: T[],
  key_or_func: keyof T | ((t: T) => number)
): T | undefined {
  if (collection.length === 0) {
    return undefined;
  }

  const comparator =
    typeof key_or_func === "function"
      ? (a: T, b: T) => key_or_func(a) <= key_or_func(b)
      : (a: T, b: T) => a[key_or_func] <= b[key_or_func];

  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return collection.reduce((a, b) => (comparator(a, b) ? a : b), collection[0]);
}

export function getMinimumPrice(
  departures: NapiDeparture[],
  include_fees?: IncludedFees
): Cents {
  return departures.reduce((min_price, d) => {
    const d_price = getDepartureDisplayPrice(
      d.prices,
      false,
      null,
      include_fees
    );

    if (d_price < min_price) {
      return d_price;
    }
    return min_price;
  }, Number.MAX_SAFE_INTEGER);
}

export function getDepartureDisplayPrice(
  prices: NapiPrices,
  is_roundtrip: boolean,
  outbound_id: string | null,
  include_fees: IncludedFees = IncludedFees.NONE
): Cents {
  // one-way
  if (!is_roundtrip) {
    const fees_or_zero =
      include_fees !== IncludedFees.NONE ? prices.breakdown.fees || 0 : 0;
    const discount_or_zero = prices.breakdown.discount || 0; // Note: breakdown.discount is negative

    return prices.breakdown.base + discount_or_zero + fees_or_zero;
  }
  const is_return = !!outbound_id;

  // roundtrip outbound
  if (!is_return) {
    if (include_fees !== IncludedFees.NONE) {
      return prices.roundtrip_min_fees_included;
    }
    return prices.roundtrip_min;
  }

  // roundtrip return
  if (include_fees !== IncludedFees.NONE) {
    return prices.roundtrip_total_fees_included;
  }
  return prices.roundtrip_total;
}

export function getComparableCartPrice(
  charges: Pick<NapiV2CartCharges, "fees" | "total" | "taxes">,
  options: { include_fees?: IncludedFees } = {}
): number {
  const { include_fees = false } = options;
  const busbud_fees = charges.fees.busbud || 0;
  const supplier_fees = charges.fees.total - busbud_fees;

  const { total, taxes } = charges;

  if (include_fees === IncludedFees.ALL) {
    return total - taxes;
  }

  if (include_fees === IncludedFees.SUPPLIER_ONLY) {
    return total - taxes - busbud_fees;
  }

  return total - taxes - (busbud_fees + supplier_fees);
}

export function getDaysOffset(
  departure: Pick<NapiDeparture, "departure_time" | "arrival_time">
): number {
  // Note that timezone is irrelevant here because we want to show to the user
  // if there is a difference in *calendar* dates (YYYY-MM-DD) between the departure
  // and arrival times in their respective timezones
  const departure_time = moment(departure.departure_time);
  const arrival_time = moment(departure.arrival_time);
  return arrival_time
    .startOf("day")
    .diff(departure_time.startOf("day"), "days");
}

export function getLocationMapLink(location: NapiLocation): string {
  const address = location.address.length
    ? location.address.join(" ")
    : location.name;

  const query_data = {
    q: address,
    ll: `${location.lat},${location.lon}`
  };

  return `//maps.google.com/maps${computeQueryString(query_data)}`;
}

export function formatDepartureDuration(
  duration: moment.Duration,
  translator?: TranslatorService
): string {
  const d = duration.days();
  const h = duration.hours();
  const m = duration.minutes();

  let chars: { day: string; hour: string; minute: string };
  if (translator) {
    chars = {
      day: translator.t("!duration.days.single-char"),
      hour: translator.t("!duration.hours.single-char"),
      minute: translator.t("!duration.minutes.single-char")
    };
  } else {
    chars = { day: "d", hour: "h", minute: "m" };
  }

  if (d > 0) {
    return d + `${chars.day} ` + (h > 0 ? h + chars.hour : "");
  }

  return (
    (h > 0 ? h + chars.hour + (m > 0 ? " " : "") : "") +
    (m > 0 ? m + chars.minute : "")
  );
}

export function formatDurationFromMinutes(
  duration_in_minutes: number,
  translator: TranslatorService
): string {
  return formatDepartureDuration(
    getMomentDurationInMinutes(duration_in_minutes),
    translator
  );
}

export const buildNextDepartures = (
  route_departures: NextDeparture[],
  currency_code: CurrencyCode,
  max_number_of_departures: number
): NextDepartures | null => {
  if (!route_departures.length) {
    return null;
  }

  return {
    buses_per_day: sumBy(route_departures, "key_stats.avg_num_departures"),
    min_price: min(map(route_departures, "key_stats.min_price")) || 0,
    currency: currency_code,
    operator_names: uniq(
      route_departures.flatMap(route => route.key_stats?.profiles ?? [])
    ).slice(0, max_number_of_departures),
    routes: route_departures
  };
};

export const extractDeparturesFromPriceCalendar = (
  departures_per_day: DeparturesPerDate[]
): PriceCalendarDeparture[] => {
  return departures_per_day.flatMap(
    departures_per_day => departures_per_day.departures ?? []
  );
};

export const getLowestPricedDeparture = (
  departures: PriceCalendarDeparture[]
): PriceCalendarDeparture | undefined =>
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  minBy(departures, dep => dep.prices[0].total);

export const getLowestPricedDeparturesByOperator = (
  departures: PriceCalendarDeparture[]
): PriceCalendarDeparture[] => {
  const departures_without_interconnections = departures.filter(
    dep => dep.type === DepartureTypes.udm_departure
  );
  return map(
    groupBy(departures_without_interconnections, "id"),
    getLowestPricedDeparture
  ).filter(isNotNil);
};

export const getTodayDepartures = (
  departures: PriceCalendarDeparture[]
): PriceCalendarDeparture[] => {
  const today = moment();

  return departures.filter(d => moment(d.departure_time).isSame(today, "day"));
};

export function isBusFamilyVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Bus | VehicleType.Shuttle | VehicleType.Van {
  return [VehicleType.Bus, VehicleType.Shuttle, VehicleType.Van].includes(
    vehicle_type
  );
}

export function isBusVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Bus {
  return vehicle_type === VehicleType.Bus;
}

export function isTrainVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Train {
  return vehicle_type === VehicleType.Train;
}

export function isFerryVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Ferry {
  return vehicle_type === VehicleType.Ferry;
}

export function isCarpoolVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Carpool {
  return vehicle_type === VehicleType.Carpool;
}

export function isShuttleVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Shuttle {
  return vehicle_type === VehicleType.Shuttle;
}

export function isVanVehicle(
  vehicle_type: VehicleType
): vehicle_type is VehicleType.Van {
  return vehicle_type === VehicleType.Van;
}

export function isTrainStation(
  location_type: LocationType
): location_type is LocationType.TrainStation {
  return location_type === LocationType.TrainStation;
}

export function isBusStop(
  location_type: LocationType
): location_type is LocationType.BusStop {
  return location_type === LocationType.BusStop;
}

export function isBusStation(
  location_type: LocationType
): location_type is LocationType.BusStation {
  return location_type === LocationType.BusStation;
}

/**
 * The purpose of this function is to get the minimum price on prices calendar.
 *
 * @param prices_calendar
 */
export function getPriceCalendarMinPrice(
  prices_calendar: DeparturesPerDate[] | TopDeparturesPerDate[] | null
): Price | null {
  const departures = prices_calendar
    ? extractDeparturesFromPriceCalendar(prices_calendar)
    : [];
  const prices = departures
    .map(d => d.prices ?? null)
    .filter(price => !!price[0]?.breakdown?.base);

  if (!prices.length) {
    return null;
  }

  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const minimum_price = minBy(prices, p => p[0].breakdown.base);
  if (!minimum_price) {
    return null;
  }
  return {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    cents: minimum_price[0].breakdown.base,
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    currency: minimum_price[0].currency
  };
}

/**
 * Returns the best departure based on the criteria, or the first departure if there are only ties
 *
 * @param dep_a
 * @param dep_b
 * @param criteria
 */
export function chooseBestDeparture(
  dep_a: NapiDepartureResults,
  dep_b: NapiDepartureResults,
  criteria: SortComparatorCriteria[]
): NapiDepartureResults {
  const comparison_result = baseSortComparator(
    adaptDepartureToSortItem(dep_a, CHEAPEST_BASE),
    adaptDepartureToSortItem(dep_b, CHEAPEST_BASE),
    criteria
  );

  if (comparison_result > 0) {
    return dep_b;
  }
  return dep_a;
}

export function getComparablePrice<T extends NapiDepartureBase>(
  departure: T
): number {
  return (
    departure.prices.breakdown.base - (departure.prices.breakdown.discount || 0)
  );
}
